import { GermanLangKey, useTranslation } from '../../useTranslation'
import { Link, useLocation } from 'react-router-dom'
import logo from '../../../src/assets/logo.svg'
import { useContext, useMemo, useState } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faBars, faXmark } from '@fortawesome/free-solid-svg-icons'
import { useUser } from '../../common/hooks/auth/useUser'
import { GlobalContext } from '../../common/global-context'

interface MainNavProps {
    showConfiguratorNav?: boolean
    responsive?: boolean
}

const MainNav = ({ showConfiguratorNav, responsive }: MainNavProps) => {
    const [isOpenHamburgerMenu, setIsOpenHamburgerMenu] = useState(false)
    const { t, i18n } = useTranslation()
    const location = useLocation()
    const { settings } = useContext(GlobalContext)
    const navCurrentPage = location.pathname.replace('/', '')
    const { user, isVerified } = useUser()

    const defaultMenuItems = [
        {
            id: '',
            name: t('homePage.aboutUs'),
            link:
                i18n.language === GermanLangKey ? 'https://www.heimerle-meule.com/de/unternehmen/ueber-uns' : 'https://www.heimerle-meule.com/company/about-us',
            internal: false,
        },
        {
            id: '',
            name: t('homePage.news'),
            link: i18n.language === GermanLangKey ? 'https://www.heimerle-meule.com/de/unternehmen/news' : 'https://www.heimerle-meule.com/company/news',
            internal: false,
        },
        {
            id: '',
            name: t('homePage.responsibility'),
            link:
                i18n.language === GermanLangKey
                    ? 'https://www.heimerle-meule.com/de/unternehmen/verantwortung/politik-bzgl-konfliktmineralien'
                    : 'https://www.heimerle-meule.com/company/responsibility/policy-concerning-conflict-minerals',
            internal: false,
        },
        {
            id: '',
            name: t('homePage.stores&Configurators'),
            link:
                i18n.language === GermanLangKey
                    ? 'https://www.heimerle-meule.com/de/online-spezial/downloads/allgemein'
                    : 'https://www.heimerle-meule.com/online-special/downloads/general',
            internal: false,
        },
    ]

    const configuratorMenuItems = useMemo(() => {
        const commonItems = []

        if (!settings?.maintenanceMode) {
            commonItems.push({
                id: 'configurator',
                name: t('navLink.configuration'),
                link: '/configurator',
                internal: true,
            })
        }

        commonItems.push({
            id: 'alloy-info',
            name: t('navLink.alloyInformation'),
            link: '/alloy-info',
            internal: true,
        })

        if (user?.showBulkImport && isVerified && !settings?.maintenanceMode) {
            commonItems.splice(1, 0, {
                id: 'table-input',
                name: t('navLink.tableInput'),
                link: '/table-input',
                internal: true,
            })
        }

        return commonItems
    }, [user, t])

    const menuItems = showConfiguratorNav ? configuratorMenuItems : defaultMenuItems

    return (
        <div className="main-nav w-full border-b border-[#e2e2e2]">
            <div
                className={`relative container h-24 flex items-center justify-start gap-11 pt-1 max-tablet:w-full max-tablet:max-w-full max-tablet:px-7 ${
                    responsive && 'max-lg:justify-between'
                }`}
            >
                <Link to="/" data-cy="main-nav-logo">
                    <img src={logo} className="logo h-12" alt="Logo" />
                </Link>
                <FontAwesomeIcon
                    icon={isOpenHamburgerMenu ? faXmark : faBars}
                    className={`hamburger-menu text-custom_blue hidden! cursor-pointer text-2xl ${responsive && 'max-lg:block!'}`}
                    onClick={() => setIsOpenHamburgerMenu((prev) => !prev)}
                    data-cy="main-nav-menu-toggle"
                />
                <nav
                    className={`nav-list pt-2 -mt-0.5 flex items-center justify-start gap-11 ${
                        responsive &&
                        `max-lg:absolute max-lg:items-end max-lg:gap-4 max-lg:flex-col max-lg:z-10 max-lg:bg-white max-lg:p-4 max-lg:shadow-custom-box max-lg:top-full max-lg:right-3 max-lg:mt-0 max-lg:drop-shadow-custom ${
                            isOpenHamburgerMenu ? 'max-lg:flex' : 'max-lg:hidden'
                        }`
                    }`}
                    data-cy="main-nav-list"
                >
                    {menuItems.map((item, index) => {
                        if (item.internal) {
                            return (
                                <Link
                                    to={item.link}
                                    key={item.name}
                                    data-cy={`main-nav-link-${item.id}`}
                                    className={`text-custom_blue text-base pb-1 font-light hover:text-blue-800 border-b-2 ${
                                        item.id === navCurrentPage ? 'border-[#000f59]' : ' border-transparent'
                                    }`}
                                >
                                    {item.name}
                                </Link>
                            )
                        } else {
                            return (
                                <a
                                    href={item.link}
                                    key={item.name}
                                    data-cy={`main-nav-external-link-${index}`}
                                    className="text-custom_blue text-base font-light hover:text-blue-800 border-b-2 border-transparent pb-1"
                                    target="_blank"
                                >
                                    {item.name}
                                </a>
                            )
                        }
                    })}
                </nav>
            </div>
        </div>
    )
}

export default MainNav
